import React from "react";
import { Box, Stack, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import SectionTitle from "./SectionTitle";
import StickyContainer from "../../components/patterns/motion/StickyContainer";
import CategoryChipsBar from "../../components/chips/CategoryChipsBar";
import EdgeFadeContainer from "../../components/Container/EdgeFadeContainer";
import LineGrid from "./LineGrid";
import CollectionCategoryCard from "./CollectionCategoryCard";
import MagazineCollectionCard from "./MagazineCollectionCard";
import CommunityJoinCard from "./CommunityJoinCard";

/**
 * MonthlyThreadsCollectionsSection
 *
 * Props:
 * @param {boolean} isMobile - 반응형 여부 [Required]
 * @param {Array} collections - 전체 컬렉션 목록 [Required]
 * @param {boolean} collectionsLoading - 컬렉션 로딩 상태 [Required]
 * @param {Array} sortedCategories - count 포함된 정렬된 카테고리 목록 [Required]
 * @param {string|null} selectedCategory - 선택된 카테고리 키 [Required]
 * @param {function} onCategoryClick - 카테고리 클릭 핸들러 [Required]
 * @param {Array} filteredCollections - 카테고리/조건 적용된 컬렉션 목록 [Required]
 * @param {function} getDisplayCategoryLabels - 컬렉션 카테고리 라벨 생성 함수 [Required]
 * @param {function} getDynamicGridSize - 카드 그리드 사이즈 계산 함수 [Required]
 */
function MonthlyThreadsCollectionsSection({
	isMobile,
	collections,
	collectionsLoading,
	sortedCategories,
	selectedCategory,
	onCategoryClick,
	filteredCollections,
	getDisplayCategoryLabels,
	getDynamicGridSize,
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const mdDown = useMediaQuery(theme.breakpoints.down("md"));
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	// Responsive AppBar height (ignore gradient): mobile 56px, desktop 64px
	const HEADER_HEIGHT_MOBILE = 56;
	const HEADER_HEIGHT_DESKTOP = 64;
	const stickyOffset = smDown ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;

	const [selectedKey, setSelectedKey] = React.useState(null);

	const totalCount = React.useMemo(() => {
		return collections.filter((c) => {
			const isPublic = c.isPublic ?? true;
			return isPublic && c.title && c.title.length > 0;
		}).length;
	}, [collections]);

	// 약한 셔플: 각 아이템은 최대 3칸 이내에서 이동, 래핑 없음
	const lightlyShuffle = React.useCallback((list) => {
		const n = list.length;
		if (n <= 3) return list;
		const result = list.slice();
		const maxOps = Math.min(3, Math.max(1, Math.floor(n / 10)));
		let ops = 0;
		while (ops < maxOps) {
			const i = Math.floor(Math.random() * n);
			const dir = Math.random() < 0.5 ? -1 : 1;
			const distance = 1 + Math.floor(Math.random() * 3); // 1..3
			let j = i + dir * distance;
			if (j < 0) j = 0;
			if (j > n - 1) j = n - 1;
			if (j === i) {
				continue;
			}
			const [item] = result.splice(i, 1);
			result.splice(j, 0, item);
			ops += 1;
		}
		return result;
	}, []);

	const displayCollections = React.useMemo(() => {
		return lightlyShuffle(filteredCollections);
	}, [filteredCollections, lightlyShuffle]);

	// 모든 카테고리 요약용 고유 사용자 목록
	const allSummaryUsers = React.useMemo(() => {
		const usersMap = new Map();
		displayCollections.forEach((col) => {
			const uid = col.userID || col.userId || col.user_id;
			if (!uid) return;
			const id = String(uid);
			if (usersMap.has(id)) return;
			usersMap.set(id, {
				id,
				profile: {
					id,
					username: col.username || "",
					name: col.name || col.username || "",
				},
				fallbackUrl:
					col.profileImage ||
					col?.profile?.threads_profile_picture_url ||
					null,
			});
		});
		return Array.from(usersMap.values());
	}, [displayCollections]);

	// 카테고리 요약 리스트 생성: label, count, users(고유)
	const categorySummaryList = React.useMemo(() => {
		const map = new Map(); // key -> { key, label, count, users: Map }
		const labelOf = (key) => {
			const found = sortedCategories.find((c) => c.key === key);
			return (found && (found.labels?.ko || found.labels?.en)) || key;
		};

		displayCollections.forEach((col) => {
			const keys = Array.isArray(col.categoryKeys) ? col.categoryKeys : [];
			keys.forEach((k) => {
				if (!map.has(k)) {
					map.set(k, { key: k, label: labelOf(k), count: 0, users: new Map() });
				}
				const entry = map.get(k);
				entry.count += 1;
				const uid = col.userID || col.userId || col.user_id;
				if (uid) {
					const u = {
						id: String(uid),
						profile: {
							id: String(uid),
							username: col.username || "",
							name: col.name || col.username || "",
						},
						fallbackUrl:
							col.profileImage ||
							col?.profile?.threads_profile_picture_url ||
							null,
					};
					entry.users.set(u.id, u);
				}
			});
		});

		let arr = Array.from(map.values()).map((e) => ({
			key: e.key,
			label: e.label,
			count: e.count,
			users: Array.from(e.users.values()),
		}));

		// 선택 카테고리 우선 (로컬 selectedKey 우선, 없으면 상위 selectedCategory)
		const activeKey = selectedKey ?? selectedCategory;
		if (activeKey) {
			arr = arr.sort((a, b) =>
				a.key === activeKey ? -1 : b.key === activeKey ? 1 : 0
			);
		}
		// 기본 정렬: count desc
		return arr.sort((a, b) => b.count - a.count);
	}, [displayCollections, sortedCategories, selectedKey, selectedCategory]);

	// 마지막 줄 남는 span 계산 (12-grid 기준)
	// 선택된 카테고리의 컬렉션 리스트 (그리드 렌더 순서와 동일)
	const selectedCollectionsForGrid = React.useMemo(() => {
		if (!selectedKey || selectedKey === "all") return displayCollections;
		return displayCollections.filter(
			(collection) =>
				Array.isArray(collection.categoryKeys) &&
				collection.categoryKeys.includes(selectedKey)
		);
	}, [displayCollections, selectedKey]);

	const lastRowRemainder = React.useMemo(() => {
		if (!Array.isArray(selectedCollectionsForGrid) || selectedCollectionsForGrid.length === 0)
			return 0;
		let colCursor = 0;
		for (let i = 0; i < selectedCollectionsForGrid.length; i++) {
			const size = getDynamicGridSize(i) || {};
			let span = 12;
			if (mdDown) {
				span =
					typeof size.xs === "number"
						? size.xs
						: typeof size.sm === "number"
						? size.sm
						: 12;
			} else {
				span =
					typeof size.md === "number"
						? size.md
						: typeof size.lg === "number"
						? size.lg
						: typeof size.xl === "number"
						? size.xl
						: 12;
			}
			span = Math.max(1, Math.min(12, span));
			if (span > 12 - colCursor) {
				// 새 줄로 넘어감
				colCursor = 0;
			}
			colCursor += span;
			if (colCursor >= 12) {
				colCursor = 0;
			}
		}
		return colCursor === 0 ? 0 : 12 - colCursor;
	}, [selectedCollectionsForGrid, getDynamicGridSize, mdDown]);

	// '모두보기' 카테고리 요약 그리드의 마지막 줄 남는 span 계산
	const categoryLastRowRemainder = React.useMemo(() => {
		if (!Array.isArray(categorySummaryList)) return 0;
		const totalItems = categorySummaryList.length + 1; // '모든 카테고리' 카드 포함
		if (totalItems === 0) return 0;
		let colCursor = 0;
		for (let i = 0; i < totalItems; i++) {
			let span;
			if (isMobile) {
				span = 6; // xs=6 (2열)
			} else {
				// PC: 상위 2개는 6, 다음 3개는 4, 나머지는 3
				if (i < 2) span = 6;
				else if (i < 5) span = 4;
				else span = 3;
			}
			if (span > 12 - colCursor) {
				colCursor = 0;
			}
			colCursor += span;
			if (colCursor >= 12) {
				colCursor = 0;
			}
		}
		return colCursor === 0 ? 0 : 12 - colCursor;
	}, [categorySummaryList, isMobile]);

	return (
		<Stack spacing={4}>
			<SectionTitle title={t("allCollection.title")} />
			<Box id="collections" sx={{ overflow: "visible" }}>
				<StickyContainer
					position="top"
					offset={stickyOffset}
					detectSticky={true}
					activeStyle={{}}
					sx={{ backgroundColor: "background.default" }}
				>
					<EdgeFadeContainer>
						<Stack direction="row" spacing={0}>
						<CategoryChipsBar
								totalCount={totalCount}
								categories={sortedCategories.map((cat) => ({
									key: cat.key,
									label:
										(cat.labels && (cat.labels.ko || cat.labels.en)) || cat.key,
									count: cat.count,
								}))}
								selectedCategory={selectedKey}
								onCategoryClick={(key) => {
								if (key === "all") setSelectedKey("all");
									else setSelectedKey(key);
								}}
								isMobile={isMobile}
							/>
						</Stack>
					</EdgeFadeContainer>
				</StickyContainer>

				<Box sx={{ width: "100%", overflow: "hidden" }}>
					{collectionsLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								padding: "2rem",
							}}
						>
							로딩 중...
						</div>
					) : selectedKey === null ? (
						<LineGrid
							gap={16}
							borderColor="primary.main"
							gridColumns={isMobile ? 2 : 4}
						>
						{/* '모든 카테고리' 카드 */}
						<Grid key="all-categories" size={isMobile ? { xs: 6, sm: 6 } : { md: 6, lg: 6 }}>
							<CollectionCategoryCard
								label={"모든 카테고리"}
								count={totalCount}
								users={allSummaryUsers}
								isMobile={isMobile}
								onClick={() => setSelectedKey("all")}
							/>
						</Grid>
						{categorySummaryList.map((cat, index) => {
							const effectiveIndex = index + 1; // '모든 카테고리'가 첫 칸
							const gridSize = isMobile
								? { xs: 6, sm: 6 }
								: effectiveIndex < 2
								? { md: 6, lg: 6 }
								: effectiveIndex < 5
								? { md: 4, lg: 4 }
								: { md: 3, lg: 3 };
							return (
								<Grid key={cat.key || index} size={gridSize}>
									<CollectionCategoryCard
										label={cat.label}
										count={cat.count}
										users={cat.users}
										isMobile={isMobile}
										onClick={() => setSelectedKey(cat.key)}
									/>
								</Grid>
							);
						})}
					{/** 카테고리 요약 그리드 마지막 줄 채우기 */}
					{categoryLastRowRemainder > 0 && (
						<Grid
							key="community-join-filler-summary"
							size={isMobile ? { xs: categoryLastRowRemainder } : { md: categoryLastRowRemainder }}
						>
							<CommunityJoinCard isMobile={isMobile} />
						</Grid>
					)}
						</LineGrid>
                    ) : (
						<LineGrid
							gap={16}
							borderColor="primary.main"
							gridColumns={isMobile ? 2 : 4}
						>
					{selectedCollectionsForGrid
						.map((collection, index) => {
									const userId =
										collection.userID ||
										collection.userId ||
										collection.user_id;
									const user = {
										id: String(userId || ""),
										profile: {
											id: String(userId || ""),
											username: collection.username || "",
											name: collection.name || collection.username || "",
										},
									};
									const gridSize = getDynamicGridSize(index);
									return (
										<Grid key={collection.id || index} size={gridSize}>
											<MagazineCollectionCard
												collection={collection}
												user={user}
												isMobile={isMobile}
												size={
													!isMobile &&
													((gridSize && gridSize.lg === 6) ||
														(gridSize && gridSize.md === 6))
														? "large"
														: "default"
												}
												categoryLabels={getDisplayCategoryLabels(collection)}
											/>
										</Grid>
									);
                                })}
                            {/** 마지막 row가 비어 있으면 커뮤니티 참여 카드로 채우기 */}
                            {lastRowRemainder > 0 && (
                                <Grid
                                    key="community-join-filler"
                                    size={
                                        mdDown
                                            ? { xs: lastRowRemainder }
                                            : { md: lastRowRemainder }
                                    }
                                    sx={{
                                        // LineGrid의 시각 규칙을 유지: 우측 경계 정렬을 위한 미세 보정은 LineGrid가 수행
                                    }}
                                >
                                    <CommunityJoinCard isMobile={isMobile} />
                                </Grid>
                            )}
						</LineGrid>
					)}

					{/* ‘모두보기’ 상태에서만 하단 카드를 보이도록 유지하려면 주석 해제
                    {selectedKey === null && (
                      <Box sx={{ width: '100%', overflow: 'hidden', mt: 4 }}>
                        ...
                      </Box>
                    )}
                    */}
				</Box>
			</Box>
		</Stack>
	);
}

export default MonthlyThreadsCollectionsSection;
